import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ComunicadoResponse } from 'app/shared/models/ei-models/comunicado/_index';
import { GlobalService } from 'app/shared/services/core';
import { ComunicadoService } from 'app/shared/services/edduca-imob';
import { ComunicadoModalComponent } from './comunicado-modal/comunicado-modal.component';
import { ComunicadoRegisterComponent } from './comunicado-register/comunicado-register.component';
import { AuthService } from 'app/core/auth/auth.service';
import { ImobiliariaService } from 'app/shared/services/edduca-imob/imobiliaria/imobiliaria.service';
import { ImobiliariaForAutocompleteResponse } from 'app/shared/models/ei-models';
import { PerfilEnum } from 'app/shared/models/core/security/perfil.enum';
import { NotificationService } from 'app/shared/components/notification';
import { GamificacaoHandler } from 'app/shared/components/gamificacao';
import { useMenuTracking } from 'app/shared/hooks';

@Component({
  selector: 'app-comunicados',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule
  ],
  templateUrl: './comunicados.component.html',
  styleUrl: './comunicados.component.scss'
})
export class ComunicadosComponent implements OnInit {
  private _fb = inject(FormBuilder);
  private _authService = inject(AuthService);
  private _globalService = inject(GlobalService);
  private _comunicadoService = inject(ComunicadoService);
  private _imobiliariaService = inject(ImobiliariaService);
  private _notification = inject(NotificationService);
  private _dialog = inject(MatDialog);
  private _gamificacaoHandler = inject(GamificacaoHandler);
  private menuTracking = useMenuTracking();

  // Tab ativa
  activeTab: 'recebidos' | 'enviados' | 'todos' = 'recebidos';

  // Dados
  comunicadosRecebidos: ComunicadoResponse[] = [];
  comunicadosEnviados: ComunicadoResponse[] = [];
  comunicadosImobiliaria: ComunicadoResponse[] = []; // Para ADM_SISTEMA e PARCEIRO

  // Estados
  loadingRecebidos = false;
  loadingEnviados = false;
  loadingImobiliaria = false;

  // Info do usuário
  usuarioLogadoId: number = 0;
  imobiliariaId: number = 0;
  perfilId: number = 0;

  // Modal de detalhes
  comunicadoSelecionado: ComunicadoResponse | null = null;
  showDetalheModal = false;

  // Autocomplete de imobiliárias (para ADM_SISTEMA e PARCEIRO)
  imobiliariaCtrl = new FormControl<ImobiliariaForAutocompleteResponse | null>(null);
  imobiliariaOptions: ImobiliariaForAutocompleteResponse[] = [];
  filteredImobiliarias: ImobiliariaForAutocompleteResponse[] = [];

  // Filtro de resultado
  resultFilter = new FormControl('');
  filteredComunicadosRecebidos: ComunicadoResponse[] = [];
  filteredComunicadosEnviados: ComunicadoResponse[] = [];
  filteredComunicadosImobiliaria: ComunicadoResponse[] = [];

  /**
   * Verifica se o usuário é ADM_SISTEMA ou PARCEIRO (modo visualização)
   */
  get isVisualizacaoMode(): boolean {
    return this.perfilId === PerfilEnum.ADM_SISTEMA || this.perfilId === PerfilEnum.PARCEIRO;
  }

  /**
   * Verifica se o usuário pode criar comunicados (ADM_SISTEMA ou ADM_IMOBILIARIA)
   */
  get podeCriarComunicado(): boolean {
    return this.perfilId === PerfilEnum.ADM_SISTEMA || this.perfilId === PerfilEnum.ADM_IMOBILIARIA;
  }

  /**
   * Nome da imobiliária selecionada (para exibição no header)
   */
  get selectedImobiliariaName(): string | null {
    return this.imobiliariaCtrl.value?.nome || null;
  }

  ngOnInit(): void {
    // Registrar acesso ao menu para gamificação
    this.menuTracking.registrarAcessoMenu('comunicados');

    const user = this._authService.getTokenInfo();
    if (!user) {
      this._notification.warn('Sessão não encontrada. Faça login novamente.');
      return;
    }

    // Converte para número pois JWT retorna strings
    this.usuarioLogadoId = Number(this._globalService.getIdUserLogged());
    this.imobiliariaId = Number(user.imobiliariaId);
    this.perfilId = Number(user.perfilId);

    // Setup do filtro
    this.resultFilter.valueChanges.subscribe(value => {
      this.applyFilter(value || '');
    });

    if (this.isVisualizacaoMode) {
      // ADM_SISTEMA ou PARCEIRO: carrega lista de imobiliárias para o autocomplete
      this.activeTab = 'todos';
      this.loadImobiliarias();
    } else {
      // ADM_IMOBILIARIA ou USUARIO_IMOBILIARIA: carrega comunicados do usuário
      this.carregarComunicadosRecebidos();
      this.carregarComunicadosEnviados();
    }
  }

  /**
   * Aplica filtro nos comunicados
   */
  private applyFilter(value: string): void {
    const filterValue = value.toLowerCase().trim();

    if (this.isVisualizacaoMode) {
      this.filteredComunicadosImobiliaria = this.comunicadosImobiliaria.filter(c =>
        c.titulo.toLowerCase().includes(filterValue) ||
        c.textoComunicado?.toLowerCase().includes(filterValue) ||
        c.nomeRemetente?.toLowerCase().includes(filterValue)
      );
    } else {
      this.filteredComunicadosRecebidos = this.comunicadosRecebidos.filter(c =>
        c.titulo.toLowerCase().includes(filterValue) ||
        c.textoComunicado?.toLowerCase().includes(filterValue) ||
        c.nomeRemetente?.toLowerCase().includes(filterValue)
      );
      this.filteredComunicadosEnviados = this.comunicadosEnviados.filter(c =>
        c.titulo.toLowerCase().includes(filterValue) ||
        c.textoComunicado?.toLowerCase().includes(filterValue)
      );
    }
  }

  /**
   * Carrega lista de imobiliárias para o autocomplete
   */
  loadImobiliarias(): void {
    this._imobiliariaService.getAllForAutocomplete().subscribe({
      next: (imobiliarias) => {
        this.imobiliariaOptions = imobiliarias;
        this.filteredImobiliarias = imobiliarias;
      },
      error: () => {
        this._notification.error('Falha ao carregar lista de imobiliárias');
      }
    });
  }

  /**
   * Filtra imobiliárias no autocomplete
   */
  filterImobiliarias(event: Event): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredImobiliarias = this.imobiliariaOptions.filter(imob =>
      imob.nome.toLowerCase().includes(value)
    );
  }

  /**
   * Exibe nome da imobiliária no input
   */
  displayImobiliariaFn(imobiliaria: ImobiliariaForAutocompleteResponse): string {
    return imobiliaria?.nome || '';
  }

  /**
   * Callback quando uma imobiliária é selecionada
   */
  onImobiliariaSelected(imobiliaria: ImobiliariaForAutocompleteResponse): void {
    this.imobiliariaId = imobiliaria.id;
    this.carregarComunicadosImobiliaria();
  }

  /**
   * Carrega todos os comunicados da imobiliária selecionada (para ADM_SISTEMA e PARCEIRO)
   */
  carregarComunicadosImobiliaria(): void {
    if (!this.imobiliariaId) return;

    this.loadingImobiliaria = true;
    this._comunicadoService.listarPorImobiliaria(this.imobiliariaId).subscribe({
      next: (comunicados) => {
        this.comunicadosImobiliaria = comunicados || [];
        this.filteredComunicadosImobiliaria = this.comunicadosImobiliaria;
        this.loadingImobiliaria = false;
      },
      error: () => {
        this.comunicadosImobiliaria = [];
        this.filteredComunicadosImobiliaria = [];
        this.loadingImobiliaria = false;
        this._notification.error('Erro ao carregar comunicados da imobiliária.');
      }
    });
  }

  /**
   * Carrega comunicados recebidos pelo usuário
   */
  carregarComunicadosRecebidos(): void {
    this.loadingRecebidos = true;
    this._comunicadoService.listarRecebidos(this.usuarioLogadoId).subscribe({
      next: (comunicados) => {
        this.comunicadosRecebidos = comunicados || [];
        this.filteredComunicadosRecebidos = this.comunicadosRecebidos;
        this.loadingRecebidos = false;
      },
      error: () => {
        this.comunicadosRecebidos = [];
        this.filteredComunicadosRecebidos = [];
        this.loadingRecebidos = false;
      }
    });
  }

  /**
   * Carrega comunicados enviados pelo usuário (apenas ADM_IMOBILIARIA)
   */
  carregarComunicadosEnviados(): void {
    if (this.perfilId !== PerfilEnum.ADM_IMOBILIARIA) {
      this.comunicadosEnviados = [];
      this.filteredComunicadosEnviados = [];
      return;
    }

    this.loadingEnviados = true;
    this._comunicadoService.listarEnviados(this.usuarioLogadoId).subscribe({
      next: (comunicados) => {
        this.comunicadosEnviados = comunicados || [];
        this.filteredComunicadosEnviados = this.comunicadosEnviados;
        this.loadingEnviados = false;
      },
      error: () => {
        this.comunicadosEnviados = [];
        this.filteredComunicadosEnviados = [];
        this.loadingEnviados = false;
      }
    });
  }

  /**
   * Abre o modal de detalhes do comunicado
   */
  abrirDetalhe(comunicado: ComunicadoResponse): void {
    // Se não foi visualizado, marca como visualizado
    if (!comunicado.visualizadoFlag && !this.isVisualizacaoMode) {
      this._comunicadoService.marcarVisualizado(comunicado.id, this.usuarioLogadoId).subscribe({
        next: (response) => {
          comunicado.visualizadoFlag = true;
          // Processar gamificação se houver pontos
          if (response?.gamificacao) {
            this._gamificacaoHandler.processarGamificacao(response.gamificacao);
          }
        }
      });
    }

    this.comunicadoSelecionado = comunicado;
    this.showDetalheModal = true;
  }

  /**
   * Fecha o modal de detalhes
   */
  fecharDetalhe(): void {
    this.comunicadoSelecionado = null;
    this.showDetalheModal = false;
  }

  /**
   * Abre o formulário para criar novo comunicado
   */
  abrirForm(): void {
    this._dialog.open(ComunicadoRegisterComponent, {
      panelClass: 'modal-dark',
      width: '60vw',
      maxWidth: '800px',
      data: {
        perfilId: this.perfilId,
        imobiliariaId: this.imobiliariaId,
        usuarioLogadoId: this.usuarioLogadoId
      }
    }).afterClosed().subscribe(reload => {
      if (reload) {
        if (this.isVisualizacaoMode) {
          this.carregarComunicadosImobiliaria();
        } else {
          this.carregarComunicadosRecebidos();
          this.carregarComunicadosEnviados();
        }
      }
    });
  }

  /**
   * Retorna a origem do comunicado para exibição
   */
  getOrigem(comunicado: ComunicadoResponse): string {
    if (comunicado.isEdducaImob) {
      return 'EdducaImob';
    }
    return comunicado.nomeRemetente || 'Administrador';
  }

  /**
   * Retorna a classe CSS para a origem
   */
  getOrigemClass(comunicado: ComunicadoResponse): string {
    if (comunicado.isEdducaImob) {
      return 'text-[#E4811E] font-semibold';
    }
    return 'text-white';
  }
}